import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontIcon } from '@fluentui/react';
import { useSearchParams, useNavigate, useLocation } from 'react-router';
import { useAppContext } from './AppContext';
import SConsciousApiService from '../services/SConsciousApiService';
import { IProductItem } from '../../../shared/interfaces/IReceipAnalyzerEntities';
import { IBillSplit, IBillSplitParticipant, IItemClaim } from '../../../shared/interfaces/IBillSplit';
import { IFriend } from '../../../shared/interfaces/IFriend';

/** Parse item_ids from either legacy string[] or new {id,qty}[] JSON */
function parseClaims(itemIds?: string): IItemClaim[] {
    if (!itemIds) return [];
    const parsed = JSON.parse(itemIds);
    if (!Array.isArray(parsed) || parsed.length === 0) return [];
    if (typeof parsed[0] === 'string') return parsed.map((id: string) => ({ id, qty: 1 }));
    return parsed;
}

interface IChatMsg {
    messageId: string;
    fromUserId: string;
    fromName: string;
    message: string;
    createdAt: string;
    attachmentName?: string;
}

interface WizardParticipant {
    user_id?: string;
    email: string;
    name: string;
    item_ids: string[];
    amount: number;
}

const POLL_INTERVAL = 4000;

export default function BillSplitLive() {
    const { appData } = useAppContext();
    const userId = appData?.user?.userId || '';
    const userName = appData?.user?.userName || appData?.user?.userEmail || 'You';
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const splitId = searchParams.get('splitId') || '';
    const receiptId = searchParams.get('receiptId') || '';

    // Receipt data passed via navigation state (from ReceiptSummary)
    const navState = location.state as { items?: IProductItem[]; totalAmount?: number; currencyCode?: string } | null;

    const api = useRef(new SConsciousApiService()).current;

    // ── Live split state ──────────────────────────────────────────
    const [split, setSplit] = useState<IBillSplit | null>(null);
    const [participants, setParticipants] = useState<IBillSplitParticipant[]>([]);
    const [items, setItems] = useState<IProductItem[]>(navState?.items || []);
    const [messages, setMessages] = useState<IChatMsg[]>([]);
    const [newMsg, setNewMsg] = useState('');
    const [sending, setSending] = useState(false);
    const [activeTab, setActiveTab] = useState<'items' | 'chat'>('items');
    const [activeParticipantIdx, setActiveParticipantIdx] = useState(-1);
    const [lastUpdated, setLastUpdated] = useState('');
    const [loading, setLoading] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [unconfirming, setUnconfirming] = useState(false);
    const [accepting, setAccepting] = useState(false);
    const [reminding, setReminding] = useState(false);
    const [reminderSent, setReminderSent] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // ── Wizard state (when creating a new split) ──────────────────
    const [wizardStep, setWizardStep] = useState<'participants' | 'mode' | 'summary' | null>(null);
    const [friends, setFriends] = useState<IFriend[]>([]);
    const [wizardParticipants, setWizardParticipants] = useState<WizardParticipant[]>([]);
    const [mode, setMode] = useState<'items' | 'equal'>('equal');
    const [inviteEmail, setInviteEmail] = useState('');
    const [wizardLoading, setWizardLoading] = useState(false);
    const [wizardError, setWizardError] = useState<string | null>(null);
    const totalAmount = navState?.totalAmount || 0;
    const currencyCode = navState?.currencyCode || '';

    // Initial load — route by splitId or receiptId
    useEffect(() => {
        if (splitId) {
            // Load existing split
            api.getBillSplitById(splitId).then((data: any) => {
                if (!data?.split) { setLoading(false); return; }
                setSplit(data.split);
                setParticipants(data.participants || []);
                setItems(data.items || []);
                setLastUpdated(data.split.updated_at || '');
                const myIdx = (data.participants || []).findIndex((p: IBillSplitParticipant) => p.user_id === userId);
                setActiveParticipantIdx(myIdx >= 0 ? myIdx : -1);
                setLoading(false);
            });
            api.getBillSplitChat(splitId).then(setMessages);
        } else if (receiptId) {
            // Check for existing split on this receipt
            api.getBillSplit(receiptId).then((data: any) => {
                if (data?.split) {
                    // Redirect to the existing split
                    setSearchParams({ splitId: data.split.split_id }, { replace: true });
                } else {
                    // No split yet — show wizard
                    setWizardStep('participants');
                    setLoading(false);
                    api.getFriends(userId).then((f: IFriend[]) => {
                        setFriends(f.filter(fr => fr.status === 'registered'));
                    });
                }
            });
        } else {
            setLoading(false);
        }
    }, [splitId, receiptId]);

    // Poll for live updates
    useEffect(() => {
        if (!splitId || !split) return;
        const interval = setInterval(async () => {
            const live = await api.getBillSplitLive(splitId);
            if (live?.split) {
                if (live.split.updated_at !== lastUpdated) {
                    setSplit(live.split);
                    setParticipants(live.participants || []);
                    setLastUpdated(live.split.updated_at || '');
                }
            }
        }, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, [splitId, split, lastUpdated]);

    // Poll chat messages
    useEffect(() => {
        if (!splitId || activeTab !== 'chat') return;
        const interval = setInterval(async () => {
            const msgs = await api.getBillSplitChat(splitId);
            setMessages(msgs);
        }, 8000);
        return () => clearInterval(interval);
    }, [splitId, activeTab]);

    // Auto-scroll chat
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleClaimToggle = useCallback(async (participantIdx: number, itemId: string) => {
        const p = participants[participantIdx];
        if (!p) return;
        const currentClaims = parseClaims(p.item_ids);
        const item = items.find(it => it.item_id === itemId);
        if (!item) return;

        // How many of this item are claimed by OTHER participants?
        const othersClaimed = participants.reduce((sum, pp, i) => {
            if (i === participantIdx) return sum;
            const c = parseClaims(pp.item_ids).find(cl => cl.id === itemId);
            return sum + (c?.qty || 0);
        }, 0);
        const maxAvailable = (item.quantity || 1) - othersClaimed;

        const existing = currentClaims.find(c => c.id === itemId);
        const currentQty = existing?.qty || 0;
        const newQty = currentQty < maxAvailable ? currentQty + 1 : 0;

        const newClaims: IItemClaim[] = newQty === 0
            ? currentClaims.filter(c => c.id !== itemId)
            : existing
                ? currentClaims.map(c => c.id === itemId ? { ...c, qty: newQty } : c)
                : [...currentClaims, { id: itemId, qty: newQty }];

        const newAmount = Math.round(newClaims.reduce((sum, c) => {
            const it = items.find(x => x.item_id === c.id);
            const uPrice = it ? it.total_price / (it.quantity || 1) : 0;
            return sum + uPrice * c.qty;
        }, 0) * 100) / 100;

        // Optimistic update
        setParticipants(prev => prev.map((pp, i) =>
            i === participantIdx
                ? { ...pp, item_ids: JSON.stringify(newClaims), amount: newAmount }
                : pp
        ));

        await api.claimBillSplitItems(splitId, p.participant_id, newClaims, newAmount, userId);
    }, [participants, items, splitId, userId, api]);

    const sendChat = async () => {
        if (!newMsg.trim() || sending) return;
        setSending(true);
        const text = newMsg.trim();
        setNewMsg('');
        setMessages(prev => [...prev, {
            messageId: `local_${Date.now()}`,
            fromUserId: userId,
            fromName: userName,
            message: text,
            createdAt: new Date().toISOString(),
        }]);
        await api.sendBillSplitChat(userId, splitId, text);
        const msgs = await api.getBillSplitChat(splitId);
        setMessages(msgs);
        setSending(false);
    };

    const handleConfirmSplit = async () => {
        if (!splitId || confirming) return;
        setConfirming(true);
        const ok = await api.confirmBillSplit(userId, splitId);
        if (ok) {
            const data = await api.getBillSplitById(splitId);
            if (data?.split) {
                setSplit(data.split);
                setParticipants(data.participants || []);
            }
            const msgs = await api.getBillSplitChat(splitId);
            setMessages(msgs);
        }
        setConfirming(false);
    };

    const handleUnconfirmSplit = async () => {
        if (!splitId || unconfirming) return;
        setUnconfirming(true);
        const ok = await api.unconfirmBillSplit(userId, splitId);
        if (ok) {
            const data = await api.getBillSplitById(splitId);
            if (data?.split) {
                setSplit(data.split);
                setParticipants(data.participants || []);
            }
            const msgs = await api.getBillSplitChat(splitId);
            setMessages(msgs);
        }
        setUnconfirming(false);
    };

    const handleRemindSplit = async () => {
        if (!splitId || reminding) return;
        setReminding(true);
        await api.remindBillSplit(userId, splitId);
        setReminding(false);
        setReminderSent(true);
        setTimeout(() => setReminderSent(false), 4000);
    };

    const handleAcceptSplit = async () => {
        if (!splitId || accepting) return;
        setAccepting(true);
        const myParticipant = participants.find(p => p.user_id === userId);
        if (myParticipant) {
            const newStatus = myParticipant.status === 'accepted' ? 'pending' : 'accepted';
            // Optimistic update — only toggle status, preserve item_ids
            setParticipants(prev => prev.map(p =>
                p.participant_id === myParticipant.participant_id
                    ? { ...p, status: newStatus as IBillSplitParticipant['status'] }
                    : p
            ));
        }
        await api.acceptBillSplit(userId, splitId);
        const msgs = await api.getBillSplitChat(splitId);
        setMessages(msgs);
        setAccepting(false);
    };

    // ── Wizard helpers ────────────────────────────────────────────
    const toggleFriend = (friend: IFriend) => {
        const exists = wizardParticipants.find(p => p.user_id === friend.friend_user_id);
        if (exists) {
            setWizardParticipants(wizardParticipants.filter(p => p.user_id !== friend.friend_user_id));
        } else {
            setWizardParticipants([...wizardParticipants, {
                user_id: friend.friend_user_id,
                email: friend.friend_email,
                name: friend.friend_name || friend.friend_email,
                item_ids: [],
                amount: 0,
            }]);
        }
    };

    const addEmailInvite = () => {
        const email = inviteEmail.trim().toLowerCase();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
        if (wizardParticipants.find(p => p.email === email)) return;
        setWizardParticipants([...wizardParticipants, { email, name: email, item_ids: [], amount: 0 }]);
        setInviteEmail('');
    };

    const removeWizardParticipant = (idx: number) => {
        setWizardParticipants(wizardParticipants.filter((_, i) => i !== idx));
    };

    const handleWizardCreate = async () => {
        setWizardLoading(true);
        setWizardError(null);
        const finalParticipants = mode === 'equal'
            ? wizardParticipants.map(p => ({ ...p, amount: Math.round((totalAmount / (wizardParticipants.length + 1)) * 100) / 100 }))
            : wizardParticipants;
        const result = await api.createBillSplit(userId, receiptId, mode, totalAmount, currencyCode, finalParticipants);
        setWizardLoading(false);
        if (result?.split) {
            // Switch to live view
            setWizardStep(null);
            setLoading(true);
            setSearchParams({ splitId: result.split.split_id }, { replace: true });
        } else {
            setWizardError('Failed to create bill split');
        }
    };

    // ── Shared header ─────────────────────────────────────────────
    const backBtn = (
        <button onClick={() => navigate(-1)} style={{ background: '#000', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontFamily: 'inherit', fontSize: 16, flexShrink: 0, color: '#ffcc00' }}>←</button>
    );

    // ── Wizard screens ────────────────────────────────────────────
    if (wizardStep) {
        return (
            <div style={{ width: '100%', maxWidth: 480, margin: '0 auto', padding: '0 12px', boxSizing: 'border-box' }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0 8px' }}>
                    {backBtn}
                    <div style={{ fontSize: '0.9rem', fontWeight: 700, textAlign: 'center' }}>
                        <FontIcon iconName="People" style={{ marginRight: 6 }} />
                        Split the Bill
                    </div>
                    <div style={{ width: 36 }} />
                </div>

                {/* Step: Participants */}
                {wizardStep === 'participants' && (
                    <div>
                        {friends.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8, opacity: 0.5 }}>Friends</div>
                                {friends.map(friend => {
                                    const selected = wizardParticipants.some(p => p.user_id === friend.friend_user_id);
                                    return (
                                        <div key={friend.invite_id} onClick={() => toggleFriend(friend)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 10,
                                                padding: '8px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                                                background: selected ? '#000' : 'rgba(0,0,0,0.06)',
                                                color: selected ? '#ffcc00' : '#000',
                                                transition: 'all 0.15s ease',
                                            }}>
                                            <div style={{
                                                width: 20, height: 20, borderRadius: '50%',
                                                border: selected ? '2px solid #ffcc00' : '2px solid rgba(0,0,0,0.2)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                                            }}>
                                                {selected && '✓'}
                                            </div>
                                            <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{friend.friend_name || friend.friend_email}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8, opacity: 0.5 }}>Invite by Email</div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <input className="darkInput" placeholder="friend@email.com" value={inviteEmail}
                                    onChange={e => setInviteEmail(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && addEmailInvite()}
                                    style={{ fontFamily: 'inherit', flex: 1, fontSize: '0.82rem' }} />
                                <button className="actionButton" onClick={addEmailInvite} style={{ fontFamily: 'inherit', fontSize: '0.82rem' }}>Add</button>
                            </div>
                        </div>

                        {wizardParticipants.length > 0 && (
                            <div style={{ marginBottom: 14 }}>
                                <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8, opacity: 0.5 }}>
                                    Splitting with ({wizardParticipants.length})
                                </div>
                                {wizardParticipants.map((p, i) => (
                                    <div key={i} style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '6px 10px', borderRadius: 8, background: 'rgba(0,0,0,0.06)', marginBottom: 4,
                                    }}>
                                        <span style={{ fontSize: '0.83rem' }}>{p.name}</span>
                                        <button onClick={() => removeWizardParticipant(i)} style={{
                                            fontFamily: 'inherit', background: 'none', border: 'none', cursor: 'pointer',
                                            fontSize: '0.85rem', opacity: 0.4, padding: '0 4px',
                                        }}>✕</button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <button className="actionButton" onClick={() => setWizardStep('mode')}
                            disabled={wizardParticipants.length === 0}
                            style={{ fontFamily: 'inherit', width: '100%', fontSize: '0.85rem', opacity: wizardParticipants.length === 0 ? 0.4 : 1, justifyContent: 'center', boxSizing: 'border-box' }}>
                            Next — Choose Split Mode
                        </button>
                    </div>
                )}

                {/* Step: Mode */}
                {wizardStep === 'mode' && (
                    <div>
                        <div onClick={() => setMode('equal')}
                            style={{
                                padding: '14px 14px', borderRadius: 10, cursor: 'pointer', marginBottom: 8,
                                background: mode === 'equal' ? '#000' : 'rgba(0,0,0,0.06)',
                                color: mode === 'equal' ? '#ffcc00' : '#000',
                                transition: 'all 0.15s ease',
                            }}>
                            <div style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: 4 }}>
                                <FontIcon iconName="EqualSign" style={{ marginRight: 6 }} />Split Equally
                            </div>
                            <div style={{ fontSize: '0.78rem', opacity: 0.6 }}>
                                {totalAmount.toFixed(2)} {currencyCode} ÷ {wizardParticipants.length + 1} = {(totalAmount / (wizardParticipants.length + 1)).toFixed(2)} each
                            </div>
                        </div>

                        <div onClick={() => setMode('items')}
                            style={{
                                padding: '14px 14px', borderRadius: 10, cursor: 'pointer', marginBottom: 14,
                                background: mode === 'items' ? '#000' : 'rgba(0,0,0,0.06)',
                                color: mode === 'items' ? '#ffcc00' : '#000',
                                transition: 'all 0.15s ease',
                            }}>
                            <div style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: 4 }}>
                                <FontIcon iconName="CheckList" style={{ marginRight: 6 }} />Pick Items
                            </div>
                            <div style={{ fontSize: '0.78rem', opacity: 0.6 }}>Each person picks what they had</div>
                        </div>

                        <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => setWizardStep('participants')}
                                style={{ fontFamily: 'inherit', flex: 1, fontSize: '0.82rem', borderRadius: 20, background: 'rgba(0,0,0,0.08)', color: '#000', border: 'none', padding: '0.35rem 0.75rem', cursor: 'pointer' }}>
                                ← Back
                            </button>
                            <button className="actionButton" onClick={() => setWizardStep('summary')}
                                style={{ fontFamily: 'inherit', flex: 2, fontSize: '0.85rem', justifyContent: 'center' }}>
                                Continue
                            </button>
                        </div>
                    </div>
                )}

                {/* Step: Summary + Create */}
                {wizardStep === 'summary' && (() => {
                    const share = Math.round((totalAmount / (wizardParticipants.length + 1)) * 100) / 100;
                    return (
                        <div>
                            <div style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>You</span>
                                    <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{mode === 'equal' ? share.toFixed(2) : '—'} {currencyCode}</span>
                                </div>
                            </div>
                            {wizardParticipants.map((p, i) => (
                                <div key={i} style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 10, padding: '12px 14px', marginBottom: 6 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{p.name}</span>
                                        <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{mode === 'equal' ? share.toFixed(2) : '—'} {currencyCode}</span>
                                    </div>
                                </div>
                            ))}
                            {mode === 'items' && (
                                <div style={{ fontSize: '0.78rem', opacity: 0.5, textAlign: 'center', padding: '8px 0' }}>
                                    Everyone picks their items after creation
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 2px', fontSize: '0.82rem', opacity: 0.6, borderTop: '1px solid rgba(0,0,0,0.1)', marginTop: 8 }}>
                                <span>Receipt total</span>
                                <span>{totalAmount.toFixed(2)} {currencyCode}</span>
                            </div>

                            {wizardError && <div style={{ fontSize: '0.78rem', color: '#e53935', marginTop: 8 }}>{wizardError}</div>}

                            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                                <button onClick={() => setWizardStep('mode')}
                                    style={{ fontFamily: 'inherit', flex: 1, fontSize: '0.82rem', borderRadius: 20, background: 'rgba(0,0,0,0.08)', color: '#000', border: 'none', padding: '0.35rem 0.75rem', cursor: 'pointer' }}>
                                    ← Back
                                </button>
                                <button className="actionButton" onClick={handleWizardCreate} disabled={wizardLoading}
                                    style={{ fontFamily: 'inherit', flex: 2, fontSize: '0.85rem', justifyContent: 'center', opacity: wizardLoading ? 0.5 : 1 }}>
                                    {wizardLoading ? 'Creating…' : 'Create Split'}
                                </button>
                            </div>
                        </div>
                    );
                })()}
            </div>
        );
    }

    // ── Loading state ─────────────────────────────────────────────
    if (loading || !split) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', fontSize: '0.85rem', opacity: 0.5 }}>
                Loading bill split…
            </div>
        );
    }

    // ── Live split view ───────────────────────────────────────────
    const isSettled = split.status === 'settled';
    const isOwner = split.created_by === userId;
    const ownerName = isOwner ? 'You' : (participants.find(p => p.user_id === split.created_by)?.name || 'Owner');
    const allParticipants = participants;

    const claimedQtyMap = new Map<string, number>();       // itemId → total qty claimed across everyone
    const claimedByMap = new Map<string, { name: string; qty: number }[]>(); // itemId → [{name, qty}]
    participants.forEach(p => {
        parseClaims(p.item_ids).forEach(c => {
            claimedQtyMap.set(c.id, (claimedQtyMap.get(c.id) || 0) + c.qty);
            const arr = claimedByMap.get(c.id) || [];
            arr.push({ name: p.name || p.email, qty: c.qty });
            claimedByMap.set(c.id, arr);
        });
    });

    const ownerAmount = Math.round(items.reduce((s, it) => {
        const remainQty = (it.quantity || 1) - (claimedQtyMap.get(it.item_id) || 0);
        const uPrice = it.total_price / (it.quantity || 1);
        return s + Math.max(0, remainQty) * uPrice;
    }, 0) * 100) / 100;

    return (
        <div style={{ width: '100%', maxWidth: 480, margin: '0 auto', padding: '0 12px', boxSizing: 'border-box' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0 8px' }}>
                {backBtn}
                <div style={{ fontSize: '0.9rem', fontWeight: 700, textAlign: 'center' }}>
                    <FontIcon iconName="People" style={{ marginRight: 6 }} />
                    Bill Split
                    {(isSettled || split.status === 'confirmed') && (
                        <span style={{ marginLeft: 6, fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'rgba(0,0,0,0.7)', color: '#ffcc00' }}>
                            {isSettled ? 'Settled' : 'Confirmed'}
                        </span>
                    )}
                </div>
                <div style={{ width: 36 }} />
            </div>

            {/* Split info bar */}
            <div style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 10, padding: '10px 14px', marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{split.total_amount.toFixed(2)} {split.currency_code}</div>
                    <div style={{ fontSize: '0.72rem', opacity: 0.5 }}>{split.mode === 'equal' ? 'Split equally' : 'Pick items'} · {allParticipants.length + 1} people</div>
                </div>
                <div style={{ fontSize: '0.72rem', opacity: 0.4 }}>
                    {new Date(split.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                </div>
            </div>

            {/* Tab bar */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
                <button
                    onClick={() => setActiveTab('items')}
                    style={{
                        fontFamily: 'inherit', flex: 1, padding: '8px 0', borderRadius: 20, border: 'none', cursor: 'pointer',
                        fontWeight: 700, fontSize: '0.82rem',
                        background: activeTab === 'items' ? '#000' : 'rgba(0,0,0,0.06)',
                        color: activeTab === 'items' ? '#ffcc00' : '#000',
                    }}>
                    <FontIcon iconName="CheckList" style={{ marginRight: 4 }} />
                    Items
                </button>
                <button
                    onClick={() => setActiveTab('chat')}
                    style={{
                        fontFamily: 'inherit', flex: 1, padding: '8px 0', borderRadius: 20, border: 'none', cursor: 'pointer',
                        fontWeight: 700, fontSize: '0.82rem',
                        background: activeTab === 'chat' ? '#000' : 'rgba(0,0,0,0.06)',
                        color: activeTab === 'chat' ? '#ffcc00' : '#000',
                    }}>
                    <FontIcon iconName="Chat" style={{ marginRight: 4 }} />
                    Chat ({messages.length})
                </button>
            </div>

            {/* ── ITEMS TAB ────────────────────────────────────────── */}
            {activeTab === 'items' && split.mode === 'items' && (
                <div>
                    {/* Participant selector pills */}
                    <div style={{ display: 'flex', gap: 4, marginBottom: 10, paddingBottom: 4, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        <button
                            onClick={() => setActiveParticipantIdx(-1)}
                            style={{
                                fontFamily: 'inherit', padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
                                fontSize: '0.76rem', fontWeight: 600, whiteSpace: 'nowrap',
                                background: activeParticipantIdx === -1 ? '#000' : 'rgba(0,0,0,0.08)',
                                color: activeParticipantIdx === -1 ? '#ffcc00' : '#000',
                            }}>
                            {ownerName} ({ownerAmount.toFixed(2)})
                        </button>
                        {allParticipants.map((p, i) => {
                            const pClaims = parseClaims(p.item_ids);
                            const pAmt = Math.round(pClaims.reduce((sum, c) => {
                                const it = items.find(x => x.item_id === c.id);
                                return sum + (it ? it.total_price / (it.quantity || 1) * c.qty : 0);
                            }, 0) * 100) / 100;
                            const isMe = p.user_id === userId;
                            const isAccepted = p.status === 'accepted';
                            return (
                                <button key={p.participant_id}
                                    onClick={() => setActiveParticipantIdx(i)}
                                    style={{
                                        fontFamily: 'inherit', padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
                                        fontSize: '0.76rem', fontWeight: 600, whiteSpace: 'nowrap',
                                        background: activeParticipantIdx === i ? '#000' : 'rgba(0,0,0,0.08)',
                                        color: activeParticipantIdx === i ? '#ffcc00' : '#000',
                                    }}>
                                    {isAccepted && '✓ '}{isMe ? 'You' : (p.name || p.email)} ({pAmt.toFixed(2)})
                                </button>
                            );
                        })}
                    </div>

                    {/* Item list */}
                    {activeParticipantIdx === -1 ? (
                        /* Owner view: remaining items */
                        <div>
                            <div style={{ fontSize: '0.75rem', opacity: 0.5, marginBottom: 8 }}>
                                {ownerName}'s items (remaining after others pick)
                            </div>
                            {items.map(item => {
                                const totalQty = item.quantity || 1;
                                const claimedQty = claimedQtyMap.get(item.item_id) || 0;
                                const remainQty = Math.max(0, totalQty - claimedQty);
                                const claimedBy = claimedByMap.get(item.item_id);
                                return (
                                    <div key={item.item_id} style={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        padding: '8px 10px', borderRadius: 8, marginBottom: 3,
                                        background: remainQty === 0 ? 'rgba(0,0,0,0.03)' : 'rgba(0,0,0,0.08)',
                                        opacity: remainQty === 0 ? 0.35 : 1,
                                    }}>
                                        <div>
                                            <div style={{ fontSize: '0.82rem', fontWeight: 500 }}>{item.description}</div>
                                            {totalQty > 1 && (
                                                <div style={{ fontSize: '0.72rem', opacity: 0.5 }}>
                                                    {remainQty} of {totalQty} remaining
                                                </div>
                                            )}
                                            {claimedBy && claimedBy.map((cb, i) => (
                                                <div key={i} style={{ fontSize: '0.68rem', opacity: 0.6 }}>→ {cb.name}{cb.qty > 1 ? ` ×${cb.qty}` : ''}</div>
                                            ))}
                                        </div>
                                        <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>{(remainQty * item.total_price / (item.quantity || 1)).toFixed(2)}</span>
                                    </div>
                                );
                            })}
                            <div style={{ marginTop: 10, fontSize: '0.85rem', fontWeight: 700, textAlign: 'right' }}>
                                Total: {ownerAmount.toFixed(2)} {split.currency_code}
                            </div>
                        </div>
                    ) : (
                        /* Participant view: tap to adjust quantity */
                        <div>
                            <div style={{ fontSize: '0.75rem', opacity: 0.5, marginBottom: 8 }}>
                                Tap items for {allParticipants[activeParticipantIdx]?.user_id === userId ? 'yourself' : (allParticipants[activeParticipantIdx]?.name || 'this person')}
                            </div>
                            {items.map(item => {
                                const p = allParticipants[activeParticipantIdx];
                                const pClaims = parseClaims(p?.item_ids);
                                const myClaim = pClaims.find(c => c.id === item.item_id);
                                const myQty = myClaim?.qty || 0;
                                const totalQty = item.quantity || 1;

                                // Others' claims for this item (excluding this participant)
                                const othersQty = participants.reduce((sum, pp, i) => {
                                    if (i === activeParticipantIdx) return sum;
                                    const cls = parseClaims(pp.item_ids);
                                    const c = cls.find(cl => cl.id === item.item_id);
                                    return sum + (c?.qty || 0);
                                }, 0);
                                const otherClaimants = claimedByMap.get(item.item_id)?.filter(cb => cb.name !== (p?.name || p?.email));
                                const maxAvailable = totalQty - othersQty;
                                const selected = myQty > 0;

                                return (
                                    <div key={item.item_id}
                                        onClick={() => {
                                            if (isSettled || split.status === 'confirmed') return;
                                            const ap = allParticipants[activeParticipantIdx];
                                            if (ap?.status === 'accepted') return;
                                            handleClaimToggle(activeParticipantIdx, item.item_id);
                                        }}
                                        style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            padding: '8px 10px', borderRadius: 8, marginBottom: 3,
                                            cursor: isSettled || split.status === 'confirmed' || allParticipants[activeParticipantIdx]?.status === 'accepted' ? 'default' : 'pointer',
                                            background: selected ? '#000' : maxAvailable === 0 ? 'rgba(0,0,0,0.03)' : 'rgba(0,0,0,0.08)',
                                            color: selected ? '#ffcc00' : '#000',
                                            opacity: maxAvailable === 0 && !selected ? 0.4 : 1,
                                            transition: 'all 0.12s ease',
                                        }}>
                                        <div>
                                            <div style={{ fontSize: '0.82rem', fontWeight: 500 }}>{item.description}</div>
                                            {totalQty > 1 && (
                                                <div style={{ fontSize: '0.72rem', opacity: 0.5 }}>
                                                    {selected ? `${myQty} of ${totalQty}` : `×${totalQty}`}
                                                    {maxAvailable < totalQty && !selected ? ` (${maxAvailable} left)` : ''}
                                                </div>
                                            )}
                                            {otherClaimants && otherClaimants.length > 0 && !selected && (
                                                <div style={{ fontSize: '0.68rem', opacity: 0.6 }}>
                                                    → {otherClaimants.map(cb => `${cb.name}${cb.qty > 1 ? ` ×${cb.qty}` : ''}`).join(', ')}
                                                </div>
                                            )}
                                        </div>
                                        <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>{(myQty * item.total_price / (item.quantity || 1)).toFixed(2)}</span>
                                    </div>
                                );
                            })}
                            <div style={{ marginTop: 10, fontSize: '0.85rem', fontWeight: 700, textAlign: 'right' }}>
                                {allParticipants[activeParticipantIdx]?.user_id === userId ? 'Your' : (allParticipants[activeParticipantIdx]?.name + "'s")} total: {(() => {
                                    const pc: IItemClaim[] = allParticipants[activeParticipantIdx]?.item_ids ? JSON.parse(allParticipants[activeParticipantIdx].item_ids!) : [];
                                    return (Math.round(pc.reduce((s, c) => { const it = items.find(x => x.item_id === c.id); return s + (it ? it.total_price / (it.quantity || 1) * c.qty : 0); }, 0) * 100) / 100).toFixed(2);
                                })()} {split.currency_code}
                            </div>
                        </div>
                    )}

                    {/* Summary bar */}
                    <div style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 10, padding: '12px 14px', marginTop: 12 }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8, opacity: 0.5 }}>
                            Summary
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 4 }}>
                            <span>{ownerName}</span>
                            <span style={{ fontWeight: 700 }}>{ownerAmount.toFixed(2)}</span>
                        </div>
                        {allParticipants.map((p, i) => {
                            const pc = parseClaims(p.item_ids);
                            const pTotal = Math.round(pc.reduce((s, c) => { const it = items.find(x => x.item_id === c.id); return s + (it ? it.total_price / (it.quantity || 1) * c.qty : 0); }, 0) * 100) / 100;
                            return (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 4 }}>
                                    <span>{p.user_id === userId ? 'You' : (p.name || p.email)}</span>
                                    <span style={{ fontWeight: 700 }}>{pTotal.toFixed(2)}</span>
                                </div>
                            );
                        })}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: 6, marginTop: 6 }}>
                            <span style={{ opacity: 0.6 }}>Total</span>
                            <span style={{ fontWeight: 700 }}>
                                {(ownerAmount + allParticipants.reduce((s, p) => {
                                    const pc = parseClaims(p.item_ids);
                                    return s + Math.round(pc.reduce((ss, c) => { const it = items.find(x => x.item_id === c.id); return ss + (it ? it.total_price / (it.quantity || 1) * c.qty : 0); }, 0) * 100) / 100;
                                }, 0)).toFixed(2)} {split.currency_code}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* ── EQUAL SPLIT VIEW ──────────────────────────────── */}
            {activeTab === 'items' && split.mode === 'equal' && (
                <div>
                    <div style={{ fontSize: '0.82rem', opacity: 0.6, marginBottom: 12, textAlign: 'center' }}>
                        {split.total_amount.toFixed(2)} {split.currency_code} ÷ {allParticipants.length + 1} = {(split.total_amount / (allParticipants.length + 1)).toFixed(2)} each
                    </div>
                    <div style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 6 }}>
                            <span style={{ fontWeight: 600 }}>{ownerName}</span>
                            <span style={{ fontWeight: 700 }}>{(split.total_amount / (allParticipants.length + 1)).toFixed(2)} {split.currency_code}</span>
                        </div>
                        {allParticipants.map((p, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 6 }}>
                                <span style={{ fontWeight: 600 }}>{p.user_id === userId ? 'You' : (p.name || p.email)}</span>
                                <span style={{ fontWeight: 700 }}>{(split.total_amount / (allParticipants.length + 1)).toFixed(2)} {split.currency_code}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── CONFIRM / ACCEPT BUTTONS ───────────────────── */}
            {activeTab === 'items' && split.status === 'active' && (() => {
                const myParticipant = allParticipants.find(p => p.user_id === userId);
                const allAccepted = allParticipants.filter(p => p.user_id).every(p => p.status === 'accepted');
                const acceptedCount = allParticipants.filter(p => p.status === 'accepted').length;

                return (
                    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {/* Participant: confirm my items */}
                        {!isOwner && myParticipant && myParticipant.status !== 'accepted' && (
                            <button className="actionButton" onClick={handleAcceptSplit} disabled={accepting}
                                style={{
                                    fontFamily: 'inherit', width: '100%', fontSize: '0.85rem',
                                    justifyContent: 'center', boxSizing: 'border-box',
                                    opacity: accepting ? 0.5 : 1, padding: '10px 0',
                                }}>
                                <FontIcon iconName="CheckMark" style={{ marginRight: 6 }} />
                                {accepting ? 'Confirming…' : 'Confirm my items'}
                            </button>
                        )}
                        {!isOwner && myParticipant && myParticipant.status === 'accepted' && (
                            <button className="actionButton" onClick={handleAcceptSplit} disabled={accepting}
                                style={{
                                    fontFamily: 'inherit', width: '100%', fontSize: '0.82rem',
                                    justifyContent: 'center', boxSizing: 'border-box',
                                    opacity: accepting ? 0.5 : 1, padding: '10px 0',
                                    background: 'rgba(0,0,0,0.08)', color: '#000',
                                }}>
                                <FontIcon iconName="CheckMark" style={{ marginRight: 6, fontSize: 14 }} />
                                {accepting ? 'Updating…' : 'You confirmed your items — tap to undo'}
                            </button>
                        )}
                        {/* Owner: status + remind + confirm split */}
                        {isOwner && (
                            <>
                                <div style={{ fontSize: '0.75rem', textAlign: 'center', opacity: 0.5 }}>
                                    {acceptedCount} of {allParticipants.filter(p => p.user_id).length} confirmed their items
                                </div>
                                {allParticipants.some(p => p.status !== 'accepted') && (
                                    <button onClick={handleRemindSplit} disabled={reminding || reminderSent}
                                        style={{
                                            fontFamily: 'inherit', width: '100%', fontSize: '0.82rem',
                                            borderRadius: 20, border: 'none', cursor: reminding || reminderSent ? 'default' : 'pointer',
                                            padding: '9px 0', fontWeight: 700,
                                            background: reminderSent ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.08)',
                                            color: reminderSent ? 'rgba(0,0,0,0.4)' : '#000',
                                            opacity: reminding ? 0.5 : 1,
                                            transition: 'all 0.2s ease',
                                        }}>
                                        {reminderSent ? '✓ Reminders sent' : reminding ? 'Sending…' : '🔔 Send Reminder'}
                                    </button>
                                )}
                                <button className="actionButton" onClick={handleConfirmSplit} disabled={confirming}
                                    style={{
                                        fontFamily: 'inherit', width: '100%', fontSize: '0.85rem',
                                        justifyContent: 'center', boxSizing: 'border-box',
                                        opacity: confirming ? 0.5 : 1, padding: '10px 0',
                                    }}>
                                    <FontIcon iconName="CheckMark" style={{ marginRight: 6 }} />
                                    {confirming ? 'Confirming…' : allAccepted ? 'Everyone confirmed — Finalize Split' : 'Confirm Split'}
                                </button>
                            </>
                        )}
                    </div>
                );
            })()}
            {activeTab === 'items' && split.status === 'confirmed' && (
                <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        background: '#000', color: '#ffcc00', borderRadius: 20,
                        padding: '10px 14px', fontSize: '0.82rem', fontWeight: 700,
                    }}>
                        <FontIcon iconName="CheckMark" style={{ fontSize: 14 }} />
                        Split confirmed — receipts distributed
                    </div>
                    {isOwner && (
                        <button onClick={handleUnconfirmSplit} disabled={unconfirming}
                            style={{
                                fontFamily: 'inherit', width: '100%', fontSize: '0.78rem',
                                borderRadius: 20, border: 'none', cursor: unconfirming ? 'default' : 'pointer',
                                padding: '8px 0', fontWeight: 600,
                                background: 'rgba(0,0,0,0.08)', color: 'rgba(0,0,0,0.5)',
                                opacity: unconfirming ? 0.5 : 1,
                            }}>
                            {unconfirming ? 'Reverting…' : '↩ Unconfirm split'}
                        </button>
                    )}
                </div>
            )}

            {/* ── CHAT TAB ─────────────────────────────────────── */}
            {activeTab === 'chat' && (
                <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 280px)' }}>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
                        {messages.length === 0 && (
                            <div style={{ textAlign: 'center', fontSize: '0.82rem', opacity: 0.4, padding: 24 }}>
                                No messages yet — say hi to your split party!
                            </div>
                        )}
                        {messages.map((msg, i) => {
                            const isMe = msg.fromUserId === userId;
                            const isSystem = msg.attachmentName?.startsWith('billsplit:');
                            return (
                                <div key={msg.messageId || i} style={{
                                    display: 'flex',
                                    justifyContent: isSystem ? 'center' : isMe ? 'flex-end' : 'flex-start',
                                    marginBottom: 6,
                                }}>
                                    <div style={{
                                        maxWidth: isSystem ? '90%' : '78%',
                                        padding: isSystem ? '8px 14px' : '8px 12px',
                                        borderRadius: 14,
                                        background: isSystem ? 'rgba(0,0,0,0.06)' : isMe ? '#000' : 'rgba(0,0,0,0.08)',
                                        color: isSystem ? 'rgba(0,0,0,0.6)' : isMe ? '#ffcc00' : '#000',
                                        fontSize: isSystem ? '0.75rem' : '0.82rem',
                                        textAlign: isSystem ? 'center' : undefined,
                                    }}>
                                        {!isMe && !isSystem && (
                                            <div style={{ fontSize: '0.7rem', fontWeight: 700, opacity: 0.5, marginBottom: 2 }}>
                                                {msg.fromName}
                                            </div>
                                        )}
                                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.message}</div>
                                        <div style={{ fontSize: '0.65rem', opacity: 0.4, marginTop: 2, textAlign: 'right' }}>
                                            {new Date(msg.createdAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Chat input */}
                    <div style={{ display: 'flex', gap: 6, padding: '8px 0', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                        <input
                            className="darkInput"
                            value={newMsg}
                            onChange={e => setNewMsg(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat()}
                            placeholder="Type a message…"
                            style={{ fontFamily: 'inherit', flex: 1, fontSize: '0.82rem' }}
                        />
                        <button className="actionButton" onClick={sendChat} disabled={sending || !newMsg.trim()}
                            style={{ fontFamily: 'inherit', fontSize: '0.82rem', opacity: sending || !newMsg.trim() ? 0.4 : 1 }}>
                            Send
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
